import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Alert,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  CloudUpload as CsvIcon,
  Sync as ApiIcon,
  AutoAwesome as AutoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getBrokerById } from "../../../data/brokers";
import { useHasLinkedAccount } from "../../../hooks/useLinkedAccounts";
import Trading212ApiKeyManager from "./Trading212ApiKeyManager";
import Trading212CsvUploader from "./Trading212CsvUploader";

type ConfigurationMethod = "api" | "csv";

const Trading212ConfigurationPage = () => {
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] =
    useState<ConfigurationMethod>("api");
  const { hasAccount, account } = useHasLinkedAccount("trading212");

  const broker = getBrokerById("trading212");

  // If user already has an account connected, default to API view
  useEffect(() => {
    if (hasAccount && account) {
      setSelectedMethod("api");
    }
  }, [hasAccount, account]);

  if (!broker) {
    return <Typography>Broker not found</Typography>;
  }

  const methodInfo = {
    api: {
      title: "API Integration",
      description:
        "Connect using your Trading212 API key for automatic synchronization",
      icon: <ApiIcon sx={{ fontSize: 32 }} />,
      recommended: true,
      status: "Available Now",
    },
    csv: {
      title: "CSV Upload",
      description: "Upload your exported Trading212 transaction history",
      icon: <CsvIcon sx={{ fontSize: 32 }} />,
      recommended: false,
      status: "Available Now",
    },
  };

  const currentMethod = methodInfo[selectedMethod];

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate("/portfolio");
          }}
          sx={{ cursor: "pointer" }}
        >
          Portfolio
        </Link>
        <Typography color="text.primary">{broker.displayName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: 2,
            backgroundColor: "action.hover",
            mr: 3,
            fontSize: 28,
            color: "primary.main",
          }}
        >
          <broker.iconComponent size={28} />
        </Box>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0.5 }}>
            {broker.displayName} Integration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose how you'd like to connect your {broker.displayName} account
          </Typography>
        </Box>
      </Box>

      {/* Connection Status */}
      {hasAccount && account && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Account Connected:</strong> {account.account_nickname}
            {account.last_sync_at && (
              <>
                {" "}
                • Last sync: {new Date(account.last_sync_at).toLocaleString()}
              </>
            )}
          </Typography>
        </Alert>
      )}

      {/* Method Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <ToggleButtonGroup
              value={selectedMethod}
              exclusive
              onChange={(_, newMethod) => {
                if (newMethod !== null) {
                  setSelectedMethod(newMethod);
                }
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 4,
                  py: 2,
                  border: "2px solid",
                  borderRadius: 2,
                  mx: 1,
                  minWidth: 120,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="api">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ApiIcon />
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    API Integration
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="csv">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CsvIcon />
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    CSV Upload
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Method Overview */}
          <Fade in key={selectedMethod} timeout={300}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ color: "primary.main", mr: 2 }}>
                  {currentMethod.icon}
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h5" gutterBottom sx={{ mb: 0.5 }}>
                    {currentMethod.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentMethod.description}
                  </Typography>
                </Box>
                {currentMethod.recommended && (
                  <Box sx={{ ml: 2 }}>
                    <Alert
                      severity="info"
                      sx={{
                        py: 0.5,
                        px: 1.5,
                        "& .MuiAlert-message": { py: 0 },
                      }}
                      icon={<AutoIcon sx={{ fontSize: 16 }} />}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: "medium" }}
                      >
                        Recommended
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Box>
            </Box>
          </Fade>
        </CardContent>
      </Card>

      {/* Configuration Interface */}
      <Fade in key={selectedMethod} timeout={400}>
        <Box>
          {selectedMethod === "api" ? (
            <Trading212ApiKeyManager
              broker={broker}
              hasExistingAccount={hasAccount}
              existingAccount={account}
            />
          ) : (
            <Trading212CsvUploader broker={broker} />
          )}
        </Box>
      </Fade>

      {/* Help Section */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          borderRadius: 2,
          border: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Need help getting started?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>API Integration:</strong> Generate an API key in your
          Trading212 account → Settings → API
          <br />
          <strong>CSV Upload:</strong> Export your data from Trading212 →
          History → Transactions → Export → CSV
          <br />
          <strong>Having issues?</strong> Check our detailed setup guides or
          contact support
        </Typography>
      </Box>
    </Box>
  );
};

export default Trading212ConfigurationPage;
